import { motion } from 'framer-motion';
import { Code, Briefcase, Palette, Blocks, Brain, Smartphone } from 'lucide-react';

const Services = () => {
  const services = [
    {
      icon: Code,
      title: 'Application Development',
      description: 'Full-stack development of robust, scalable applications using modern frameworks and technologies.',
      features: [
        'React, Vue, and Angular applications',
        'Node.js, Python, and Java backends',
        'RESTful and GraphQL APIs',
        'Microservices architecture',
        'Cloud deployment (AWS, Azure, GCP)'
      ],
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Briefcase,
      title: 'Technical Project Management',
      description: 'Strategic planning and agile execution of complex technical projects from conception to delivery.',
      features: [
        'Agile/Scrum methodology',
        'Stakeholder management',
        'Resource allocation & optimization',
        'Risk assessment & mitigation',
        'Team leadership & mentoring'
      ],
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: Palette,
      title: 'Web UI Development',
      description: 'Creating beautiful, responsive, and accessible user interfaces that deliver exceptional user experiences.',
      features: [
        'Modern CSS frameworks (Tailwind, etc.)',
        'Component-based architecture',
        'Responsive & mobile-first design',
        'Accessibility (WCAG compliance)',
        'Performance optimization'
      ],
      color: 'from-orange-500 to-red-500'
    },
    {
      icon: Blocks,
      title: 'Blockchain Solutions',
      description: 'Developing decentralized applications and smart contracts leveraging blockchain technology.',
      features: [
        'Smart contract development (Solidity)',
        'DApp architecture',
        'Web3 integration',
        'NFT platforms',
        'Blockchain consulting'
      ],
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: Brain,
      title: 'AI Integration',
      description: 'Implementing AI and machine learning solutions to enhance applications with intelligent features.',
      features: [
        'Natural Language Processing',
        'Computer Vision',
        'Recommendation systems',
        'Predictive analytics',
        'AI model integration'
      ],
      color: 'from-indigo-500 to-purple-500'
    }
  ];

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="pt-32 pb-24">
      <div className="container-custom">
        {/* Header */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          className="text-center mb-16"
        >
          <h1 className="mb-6">Services</h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            Comprehensive technical expertise to transform your ideas into reality. 
            From concept to deployment, I deliver excellence at every stage.
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="space-y-12">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              transition={{ delay: index * 0.1 }}
              className="card"
            >
              <div className="flex flex-col md:flex-row gap-8">
                {/* Icon & Title */}
                <div className="md:w-1/3">
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${service.color} flex items-center justify-center mb-4`}>
                    <service.icon className="text-white" size={32} />
                  </div>
                  <h3 className="text-2xl font-display font-semibold mb-2">{service.title}</h3>
                  <p className="text-gray-600 dark:text-gray-400">{service.description}</p>
                </div>

                {/* Features */}
                <div className="md:w-2/3">
                  <h4 className="text-lg font-semibold mb-4 text-gray-700 dark:text-gray-300">Key Capabilities</h4>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <svg className="w-5 h-5 text-[rgb(var(--color-accent))] mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-600 dark:text-gray-400">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="mt-16 text-center"
        >
          <div className="inline-block p-8 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-900 rounded-2xl">
            <h3 className="text-2xl font-display font-semibold mb-4">Let's Build Something Amazing</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-2xl">
              Whether you need a complete application, technical leadership, or specialized expertise, 
              I'm here to help bring your vision to life.
            </p>
            <a href="mailto:contact@example.com" className="btn-primary">
              Get in Touch
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Services;
